/* eslint-disable camelcase */
import Axios from 'axios'

import { OAUTH_API } from '../../Configuration'
import { roundToNDigits } from '@/helpers'
import { XsrfResilient } from '@/services/Helpers'
import { goToCart } from '@/services/FramedCheckout/navigationServices'
import { arrivalWindow } from '@/components/Pdp/AddProductToCartForm/EstimatedArrival'
import {
  standardizeOrder,
  standardizeAddress,
  MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing,
} from '@/services/Standardizers'

import { constants } from '@/data'

import { log, error } from '@/services/Log'

import { STORE_CREDITS_APPLIED_FIELD } from '@/services/Configuration'
import Product from '@/services/Product'
import { generateLineItemsFromCheckout } from './generateLineItemsFromCheckout'
import getPaymentMethodFromTransactionId from './getPaymentMethodFromTransactionId'
import Facebook from '@/services/Facebook'

const getShippingAddressFromParkyOrder = (parkyOrder) => (
  parkyOrder
    ?.shipping_addresses
    ?.find?.(({ shipping_method }) => shipping_method !== constants.BOPUS_SHIPPING_DESCRIPTION)
)

const designSkus = [
  '77469310', '77496064', '77496065', '77496066', '77496067',
  '77496068', '77496069', '77496070', '77496203', '77534784',
  '77534785', '77534786', '77534787', '77534788', '77535257',
  '77535258', '77535259', '77542926', '77542927', '77542928',
  '77542929', '77542930', '77542931', '77542932', '77542933',
  '77542934', '77542935', '77542936', '77542937', '77542938',
  '77542939', '77542940', '77576646', '77576647', '77576648',
  '77576649', '77576650', '77576651',
]
const makeDesignItemInstructions = () => ({
  instructions: 'If you purchased a service package from One Kings Lane Interior Design, you can book your appointment within 6 months of purchase.',
  callToAction: {
    link: 'https://onekingslane-designservices.as.me/',
    text: 'Book your appointment',
  },
})

const productsToLineItem = async (products) => {
  const skus = products.map((product) => product.sku)
  const productsFromSkuEndpoint = await Product.bySkus(skus)
  log(`Product by SKU response`, { productsFromSkuEndpoint })

  return products.map((product, index) => {
    const name = `${product?.name || ''}`.split('~')[0]
    const { quantity, id, product_id, sku, price_ex_tax, total_ex_tax, variant_id } = product
    const isProductParentFromSearchAPI = (p) => {
      const productWarrantyHasSkuAsVariant = Object.keys(p.warranty.variants).includes(product.sku)
      return productWarrantyHasSkuAsVariant || p.upc === product.upc || product.sku === p.sku
    }
    const productFromSkuEndpoint = productsFromSkuEndpoint.find(isProductParentFromSearchAPI) || productsFromSkuEndpoint[index]
    const imageUrl = productFromSkuEndpoint?.images?.[0]?.url_standard || `https://cdn11.bigcommerce.com/s-ccb3mj27tc/products/${product_id}/images/810235/Product_Square-Product_LIA10072_Image_1__91096.1651729047.220.290.jpg?c=1`
    const variant = productFromSkuEndpoint?.warranty?.variants[sku] || ''
    const isDigital = !!productFromSkuEndpoint?.warranty?.additional?.is_digital
    return {
      name,
      editEnabled: false,
      orderComplete: true,
      couponAmount: parseFloat(product?.applied_discounts?.[0]?.amount) || 0,
      options: product.options,
      url: `/p-${name.toLowerCase().split(' ').join('-')}-${sku}?sku=${sku}`,
      salePriceAfterDiscount: parseFloat(total_ex_tax),
      extendedListPrice: parseFloat(total_ex_tax),
      quantity: quantity,
      discounts: product.applied_discounts,
      id,
      productId: id,
      sku: sku,
      isTaxable: true,
      giftWrapping: null,
      imageUrl,
      discountAmount: 0,
      listPrice: parseFloat(price_ex_tax),
      subtotalPrice: parseFloat(price_ex_tax),
      salePrice: parseFloat(price_ex_tax),
      extendedSalePrice: parseFloat(total_ex_tax),
      extendedComparisonPrice: parseFloat(total_ex_tax),
      categories: [],
      type: "physical",
      variantId: variant_id,
      socialMedia: [],
      originalPrice: parseFloat(price_ex_tax),
      isFurniture: false, //
      itemInstructions: designSkus.includes(sku) ? makeDesignItemInstructions() : {},
      arrivalWindow: isDigital ? '' : arrivalWindow({ variant }),
    }
  })
}


const createStandardizedShippingOptionFromShipment = (parkyShipment) => ({
  id: 0,
  type: 'shipping_carrier_118',
  description: parkyShipment.shipping_method,
  imageUrl: '',
  cost: parseFloat(parkyShipment?.base_cost),
  transitTime: '',
  additionalDescription: '',
  isRecommended: true,
})

const isfurnitureShipment = ({ shipping_method }) => ['ltlWhiteGlove'.toLowerCase(), 'ltlEntryway'.toLowerCase()].includes(`${shipping_method || ''}`.toLowerCase())
const tagFurnitureShipment = shipment => p => ({ ...p, isFurniture: isfurnitureShipment(shipment) })

const getProductsFromParkyShipment = async (parkyOrder, parkyShipment) => {
  let products = (
    parkyOrder?.products
      ?.filter?.(({ order_address_id: addrId }) => addrId === parkyShipment?.id)
  )
  products = await productsToLineItem(products)
  products = products?.map(tagFurnitureShipment(parkyShipment))
  return products
}

const matchShipmentTrackingToItems = ({
  shipments,
  itemIdWithinOrder,
}) => (
  shipments
    .find(
      (shipment) => shipment.items.find(
        (item) => item.order_product_id === itemIdWithinOrder
      )
    )
  || {}
)

const getConsignmentsFromOrderAndParkyOrder = async ({ parkyOrder }) => {
  const shipmentPromises = parkyOrder?.shipping_addresses?.map?.(async (shipment) => {
    const lineItems = await getProductsFromParkyShipment(parkyOrder, shipment)
    const tracking = lineItems.length
      ? matchShipmentTrackingToItems({
        shipments: parkyOrder.shipments,
        itemIdWithinOrder: lineItems[0]?.id,
      })
      : {}
    return ({
      tracking,
      id: shipment?.id,
      shippingCost: shipment?.base_cost,
      handlingCost: shipment?.base_handling_cost,
      couponDiscounts: [],
      discounts: [],
      lineItemIds: lineItems?.map(({ id }) => id),
      selectedShippingOption: createStandardizedShippingOptionFromShipment(shipment),
      shippingAddress: standardizeAddress(shipment),
      availableShippingOptions: [createStandardizedShippingOptionFromShipment(shipment)],
      lineItems,
      isBopus: shipment.shipping_method === constants.BOPUS_SHIPPING_DESCRIPTION,
      isFurniture: isfurnitureShipment(shipment),
      fullShipment: shipment,
    })
  })
  const returnValue = await Promise.all(shipmentPromises)
  return returnValue
}


export const getFinalizedOrder = XsrfResilient(async ({ orderId }) => {
  log('getFinalizedOrder for ', orderId)
  const urlParams = new URLSearchParams(window.location.search);
  let zip = urlParams.get('zip');
  try {
    const include = [
      'payments',
      'lineItems.physicalItems.socialMedia',
      'lineItems.physicalItems.options',
      'lineItems.digitalItems.socialMedia',
      'lineItems.digitalItems.options',
    ]
    const options = { params: { include: include.join(',') } }
    // Returns Order data. - https://developer.bigcommerce.com/api-reference/storefront/orders/order/ordersbyorderidget
    // This will return order data immediately after an order is placed on the storefront.
    let order = {}
    if (!zip) {
      order = (await Axios.get(`/api/storefront/orders/${orderId}`, options)).data
      zip = order?.billingAddress?.postalCode
    }
    try {
      Facebook.init()
    } catch (err) {
      error(`Pixel Failed to start ${err}`)
    }
    // GET {parky_url}/sf/order/{order-id}/{zip-code}/
    const bcResponse = (await Axios.get(`${OAUTH_API}/sf/order/${orderId}/${zip}/`))?.data?.data
    const parkyOrder = bcResponse?.order
    const paymentId = parkyOrder['payment_provider_id']

    const paymentMethod = await getPaymentMethodFromTransactionId(paymentId, parkyOrder['payment_method'], parkyOrder?.default_currency_code, parkyOrder?.total_inc_tax)

    const shippingAddress = getShippingAddressFromParkyOrder(parkyOrder)
    const consignments = await getConsignmentsFromOrderAndParkyOrder({ parkyOrder })
    const storeCreditDiscount = roundToNDigits(
      order?.payments?.find(({ providerId }) => providerId === 'storecredit')?.amount
      || order
        ?.billingAddress
        ?.customFields?.find(({ fieldId }) => fieldId === STORE_CREDITS_APPLIED_FIELD)
        ?.fieldValue,
    )

    const grandTotal = parseFloat(parkyOrder?.total_inc_tax)

    const totalFromParky = roundToNDigits(
      parseFloat(parkyOrder?.subtotal_inc_tax) + parseFloat(parkyOrder?.shipping_cost_inc_tax),
    )
    const formatCouponsFromCompleteOrder = (parkyOrder) => {
      const completeOrderCouponToOderCoupon = ({
        code = '',
        discount = 0,
        coupon_id = -1,
      } = {}) => ({
        id: coupon_id,
        code,
        "displayName": "Coupon Discount",
        "couponType": "promotion",
        discountedAmount: parseFloat(discount),
      })
      if (Array.isArray(parkyOrder?.coupons) && parkyOrder.coupons.length) {
        return [...parkyOrder.coupons].filter(f => typeof f === 'object').map(completeOrderCouponToOderCoupon)
      }
      return []
    }

    order = {
      ...order,
      status: bcResponse?.order?.status,
      customerId: order?.customerId || parkyOrder?.customer_id,
      email: order?.billingAddress?.email || parkyOrder?.billing_address?.email,
      shippingAddress,
      consignments,
      subtotal: order?.baseAmount,
      paymentMethod,
      isStoreCreditApplied: storeCreditDiscount > 0,
      grandTotal,
      outstandingBalance: storeCreditDiscount === totalFromParky ? 0 : grandTotal,
      totalDiscount: parseFloat(parkyOrder?.discount_amount || '0'),
      discountAmount: parseFloat(parkyOrder?.discount_amount || '0'),
      billingAddress: parkyOrder?.billing_address,
      taxTotal: parseFloat(parkyOrder?.total_tax),
      subtotal: parseFloat(parkyOrder?.subtotal_ex_tax),
      shippingCostTotal: (parkyOrder?.shipping_addresses || []).reduce((acc, { base_cost }) => acc + parseFloat(base_cost), 0),
      shippingCostBeforeDiscount: parseFloat(parkyOrder?.shipping_cost_ex_tax),
      orderId: parkyOrder.external_id ? `${orderId} (${parkyOrder.external_id})` : orderId,
      coupons: formatCouponsFromCompleteOrder(parkyOrder),
      completedAt: parkyOrder.date_created,
    }

    const lineItems = await productsToLineItem(parkyOrder.products)
    const augmentedLineItems = await generateLineItemsFromCheckout({
      lineItems: { physicalItems: lineItems },
    })
    const cart = {
      lineItems: augmentedLineItems,
      subtotal: parseFloat(parkyOrder?.subtotal_ex_tax),
      total: parseFloat(parkyOrder?.subtotal_inc_tax),
      discount: parseFloat(parkyOrder?.discount_amount),
      customerId: parkyOrder?.customer_id,
      email: parkyOrder?.billingAddress?.email || parkyOrder?.shipping_addresses?.[0]?.email,
      id: parkyOrder?.cart_id,
      updatedTime: parkyOrder?.date_modified,
    }
    const response = standardizeOrder({ ...order }, cart)
    await MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing({ order: response })
    log('getFinalizedOrder END ', { newOrder: response, parkyOrder, order })

    try {
      Facebook.trackPurchase({
        order_id: orderId,
        content_ids: cart.lineItems.map(({ sku }) => sku),
        contents: cart.lineItems.map(({ sku, quantity, salePrice }) => ({ id: sku, quantity, item_price: salePrice })),
        value: order.grandTotal,
        num_items: cart.lineItems.reduce((total, { quantity }) => total + quantity, 0),
        user: {
          email: order.email,
          phone: shippingAddress.phone,
        },
      })
    } catch (err) {
      error(`Pixel Failed to track purchase ${err}`)
    }
    return response
  } catch (err) {
    error(`getFinalizedOrder Failed to request API error: ${err}`, { error: err })
  }
  await goToCart()
  return { success: false }
})
